import {Component, EventEmitter, forwardRef, OnInit, Output, Input} from '@angular/core';
import { NG_VALUE_ACCESSOR, Validators, FormBuilder, FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {
  compareMatch,
  fromIndividualFormValue,
  IndividualFormValue,
  PropertyOwnerWithAddressValue,
  toIndividualFormValue,
} from '@portal-workspace/grow-shared-library';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {UntilDestroy} from '@ngneat/until-destroy';
import {delay, distinctUntilChanged, tap} from 'rxjs/operators';
import {Subject, Subscription} from 'rxjs';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import {SoleTraderValue} from '@portal-workspace/grow-shared-library';
import { MarkDirective } from '../../directives/mark-as-dirty.directive';
import { IndividualFormComponent } from '../individual-form-component/individual-form.component';

export interface SoleTraderComponentEvent {
  valid: boolean;
  entry: SoleTraderValue;
}

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    selector: 'sole-trader',
    templateUrl: './sole-trader.component.html',
    styleUrls: ['./sole-trader.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SoleTraderComponent), multi: true },
        { provide: MARK, useExisting: forwardRef(() => SoleTraderComponent) },
    ],
    standalone: true,
    imports: [MarkDirective, FormsModule, ReactiveFormsModule, IndividualFormComponent]
})
export class SoleTraderComponent extends AbstractControlValueAccessor<SoleTraderValue> implements OnInit, Mark {

  subscriptions: Subscription[] = [];

  markObservable: Subject<boolean> = new Subject<boolean>();

  formGroup: FormGroup<{
    kind: FormControl<'SoleTrader'|null>;
    individual: FormControl<IndividualFormValue>;
  }>
  formControlKind: FormControl<'SoleTrader'|null>;
  formControlIndividual: FormControl<IndividualFormValue>;

  subscription: Subscription;
  @Input({required: false}) showAddressForm = true;
  @Input({required: false}) showIdentification = false;
  @Output() events: EventEmitter<SoleTraderComponentEvent>;

  constructor(private formBuilder: FormBuilder) {
    super();
    this.events = new EventEmitter<SoleTraderComponentEvent>();
    this.formControlKind = formBuilder.control('SoleTrader');
    this.formControlIndividual = formBuilder.control(null, [Validators.required]);
    this.formGroup = formBuilder.group({
      kind: this.formControlKind,
      individual: this.formControlIndividual,
    });

    this.subscription = this.formGroup.valueChanges.pipe(
      delay(0),
      distinctUntilChanged(compareMatch),
      tap(r => {
        const entry = this.toSoleTraderValue();
        if (this.formGroup.invalid) {
          this.propagateChange(null);
        } else {
          this.propagateChange(entry);
        }
        this.events.emit({valid: this.formGroup.valid, entry})
      })
    ).subscribe();
  }

  ngOnInit() {
    setupUntilDestroy(this);
  }

  private toSoleTraderValue(): SoleTraderValue {
    const individual = fromIndividualFormValue(this.formControlIndividual.value);
    if (!individual) {
      return null;
    }
    const {mobileNumber, propertyOwner, ...rest} = individual;
    return {
      kind: 'SoleTrader',
      ...rest,
      propertyOwner: propertyOwner as Exclude<PropertyOwnerWithAddressValue, null>,
      mobile: mobileNumber ?? '',
    };
  }

  doWriteValue(v: SoleTraderValue | undefined): void | SoleTraderValue {
    this.formControlIndividual.setValue(v ? toIndividualFormValue(v) : null);
    return undefined;
  }

  mark() {
    this.formGroup.markAllAsTouched();
    this.markObservable.next(true);
  }
}
